
import { toast as showToast } from "@/hooks/use-toast";

export interface Location {
  lat: number;
  lng: number;
  name?: string;
}

// Get reverse geocode (address) from coordinates
export const getReverseGeocode = (
  lat: number, 
  lng: number, 
  onLocationSelected: (location: Location) => void,
  showQuotaToast = true
) => {
  if (!window.google || !window.google.maps) {
    console.error("Google Maps not loaded for reverse geocoding");
    onLocationSelected({ lat, lng });
    return;
  }
  
  try {
    console.log("Starting reverse geocoding for:", lat, lng);
    const geocoder = new google.maps.Geocoder();
    // Create a proper LatLng object
    const latlng = new google.maps.LatLng(lat, lng);

    // Set a timeout in case Google Maps API is slow to respond
    const timeoutId = setTimeout(() => {
      console.warn("Reverse geocoding timed out");
      onLocationSelected({ lat, lng });
    }, 5000); // 5-second timeout

    geocoder.geocode({ location: latlng }, (results, status) => {
      clearTimeout(timeoutId);
      console.log("Geocode status:", status);
      
      if (status === "OK" && results && results[0]) {
        const placeName = results[0].formatted_address;
        console.log("Found address:", placeName);
        onLocationSelected({ lat, lng, name: placeName });
      } else if (status === "OVER_QUERY_LIMIT") {
        // Handle quota exceeded error - still update location without address
        console.warn("Geocoding quota exceeded");
        onLocationSelected({ lat, lng });
        if (showQuotaToast) {
          showToast({
            title: "Address lookup limited",
            description: "Google Maps quota exceeded. Location set without address details.",
            variant: "default",
          });
        }
      } else if (status === "ZERO_RESULTS") {
        console.warn("No results found for coordinates");
        onLocationSelected({ lat, lng });
      } else {
        // For other errors, still set the location but without an address
        console.warn("Geocoding failed with status:", status);
        onLocationSelected({ lat, lng });
      }
    });
  } catch (error) {
    console.error("Error in reverse geocoding:", error);
    // Still update the location even if geocoding fails
    onLocationSelected({ lat, lng });
  }
};

// Create a marker for the map
export const createMapMarker = (
  position: google.maps.LatLng, 
  map: google.maps.Map
): google.maps.marker.AdvancedMarkerElement => {
  const markerContent = document.createElement('div');
  markerContent.innerHTML = `
    <div style="
      width: 20px;
      height: 20px;
      background-color: #FF3B30;
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      cursor: pointer;
      transform-origin: center;
      transition: transform 0.2s;
    "></div>
  `;

  const marker = new google.maps.marker.AdvancedMarkerElement({
    position,
    map,
    content: markerContent,
    title: "Drag to set location",
    gmpDraggable: true,
  });

  // Add hover effect - need to target the first element as HTMLElement
  const pinElement = markerContent.firstElementChild as HTMLElement;
  if (pinElement) {
    markerContent.addEventListener('mouseenter', () => {
      pinElement.style.transform = 'scale(1.1)';
    });
    
    markerContent.addEventListener('mouseleave', () => {
      pinElement.style.transform = 'scale(1)';
    });
  }

  return marker;
};

// Show brief instructions tooltip on the map
export const showMapInstructions = (
  map: google.maps.Map,
  marker: google.maps.Marker
): google.maps.InfoWindow | null => {
  try {
    const infoWindow = new google.maps.InfoWindow({
      content: `<div class="p-3 text-sm">
        <p class="font-semibold mb-1">Set your location:</p>
        <ul class="list-disc pl-5">
          <li>Click anywhere on the map</li>
          <li>Or drag the red marker</li>
        </ul>
      </div>`,
      maxWidth: 250
    });
    
    // Show instructions briefly when the map loads
    infoWindow.open(map, marker);
    setTimeout(() => {
      if (infoWindow) infoWindow.close();
    }, 7000);
    
    return infoWindow;
  } catch (error) {
    console.error("Error creating info window:", error);
    return null;
  }
};

// Perform geocoding from address to coordinates
export const geocodeAddress = (
  address: string,
  onLocationFound: (location: Location) => void,
  onError: (message: string) => void
) => {
  if (!address.trim()) {
    onError("Please enter a location to search");
    return;
  }
  
  // Check if we should use direct OpenStreetMap Nominatim geocoding instead of Google
  // This fallback can be more reliable when Google Maps API has issues
  if (!window.google || !window.google.maps) {
    console.log("Google Maps not available, using OSM Nominatim fallback for geocoding");
    fetchFromNominatim(address, onLocationFound, onError);
    return;
  }

  try {
    console.log("Geocoding address:", address);
    const geocoder = new google.maps.Geocoder();
    
    // Set a timeout in case the Google geocoding service is slow
    const timeoutId = setTimeout(() => {
      console.warn("Google geocoding timed out, trying OSM Nominatim fallback");
      fetchFromNominatim(address, onLocationFound, onError);
    }, 5000); // 5-second timeout

    geocoder.geocode({ address }, (results, status) => {
      clearTimeout(timeoutId);
      console.log("Geocode status for address:", status);
      
      if (status === "OK" && results && results[0]) {
        const location = results[0].geometry.location;
        const placeName = results[0].formatted_address;
        
        console.log("Found coordinates:", location.lat(), location.lng());
        console.log("Found place name:", placeName);
        
        onLocationFound({
          lat: location.lat(),
          lng: location.lng(),
          name: placeName,
        });
      } else if (status === "ZERO_RESULTS") {
        // Try OSM Nominatim as fallback if Google returns no results
        console.log("No results from Google, trying OSM Nominatim fallback");
        fetchFromNominatim(address, onLocationFound, onError);
      } else if (status === "OVER_QUERY_LIMIT") {
        console.log("Google Maps quota exceeded, trying OSM Nominatim fallback");
        fetchFromNominatim(address, onLocationFound, onError);
      } else {
        // Try OSM Nominatim as fallback
        console.log("Google geocoding failed, trying OSM Nominatim fallback");
        fetchFromNominatim(address, onLocationFound, onError);
      }
    });
  } catch (error) {
    console.error("Error in geocoding:", error);
    // Try OSM Nominatim as fallback
    fetchFromNominatim(address, onLocationFound, onError);
  }
};

// Fallback geocoding using OpenStreetMap Nominatim service
const fetchFromNominatim = (
  address: string,
  onLocationFound: (location: Location) => void,
  onError: (message: string) => void
) => {
  console.log("Using OSM Nominatim to geocode address:", address);
  
  // Use OpenStreetMap Nominatim API (free, no key required)
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`;
  
  fetch(url, {
    headers: {
      'Accept': 'application/json',
      'User-Agent': 'CrimeReport App/1.0' // Nominatim requires a user-agent
    }
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log("OSM Nominatim response:", data);
      
      if (data && data.length > 0) {
        const result = data[0];
        const location = {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          name: result.display_name
        };
        
        console.log("OSM found location:", location);
        onLocationFound(location);
      } else {
        onError("No locations found for that search term. Please try a more specific address.");
      }
    })
    .catch(error => {
      console.error("Error with OSM Nominatim search:", error);
      onError("Could not find that location. Please try a different search term.");
    });
};
